import axios from 'axios';
import { useCallback, useState } from 'react';

export interface Bank {
  bankCode: string;
  name: string;
}

interface Result {
  banks: Bank[];
  getBanks: (query: string) => Promise<Bank[]>;
  loading: boolean;
}

const EMPTY_RESULTS = [];

export const useBanks = (): Result => {
  const [bankObjects, setBankObjects] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);

  const onGetBanks = useCallback(async (query: string) => {
    if (!query) {
      setBankObjects((bankObjects) => (bankObjects?.length ? EMPTY_RESULTS : bankObjects));
    }

    setLoading(true);
    const getObjects = async () => {
      const { data } = await axios.get(process.env.INSTITUTE_FINDER_API_ENDPOINT, {
        params: {
          query,
        },
      });

      if (data.error) {
        throw data.error;
      }

      return data.institutes?.items || [];
    };

    try {
      const searchResult = await getObjects();
      const seenBankCodes = new Set();

      const result = searchResult.reduce(
        (accumulator: { bankCode: string; name: string }[], current: { bin: string; name: string }) => {
          if (seenBankCodes.has(current.bin) || `${current.bin}`.startsWith('9')) {
            return accumulator;
          }

          accumulator.push({ bankCode: `${current.bin}`, name: `${current.name}` });
          seenBankCodes.add(current.bin);

          return accumulator;
        },
        []
      );
      setBankObjects(result);
      return result;
    } catch {
      setBankObjects((bankObjects) => (bankObjects?.length ? EMPTY_RESULTS : bankObjects));

      return EMPTY_RESULTS;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    banks: bankObjects,
    getBanks: onGetBanks,
    loading,
  };
};
